import { filter } from "lodash";
import React, { Component } from "react";
import { connect, ConnectedProps } from "react-redux";
import { Subject, Subscription } from "rxjs";
import { debounceTime } from "rxjs/operators";
import styled from "styled-components";
import { TextField } from "../../../components/forms/text";
import { Flex } from "../../../components/layout";
import { Spinner } from "../../../components/loader/icon";
import { H1 } from "../../../components/typography";
import { Widget } from "../../../components/widget";
import { Pageable, Sort } from "../../../services/pageable";
import { AppStore } from "../../../store";
import { Fetchable } from "../../../store/common/fetchable/helpers";
import { LaunchConfigurationsActions } from "../../../store/launch-configurations/actions";
import { LaunchConfigurationsDTO } from "../../../store/launch-configurations/models/dto";
import { AnErrorHasOccured } from "./base";
import { PresentationalLaunchConfigurationList } from "./results";

const Header = styled(Flex)`
  justify-content: space-between;
`;

const Wrapper = styled(Flex).attrs({ centered: true })`
  justify-content: center;
  height: 20em;
`;

interface LaunchConfigurationsComponentPropsFromState {
  data: Fetchable<LaunchConfigurationsDTO.Responses.GetAll.T>;
  pagination: Pageable.T<keyof LaunchConfigurationsDTO.Responses.GetSingle.T>;
}

interface LaunchConfigurationsComponentPropsFromDispatch {
  setPagination: (
    email: string,
    sorting?: Sort<keyof LaunchConfigurationsDTO.Responses.GetSingle.T>
  ) => LaunchConfigurationsActions.Actions;
}

const connection = connect<
  LaunchConfigurationsComponentPropsFromState,
  LaunchConfigurationsComponentPropsFromDispatch,
  {},
  AppStore
>(
  (store: AppStore) => (store.launchConfigurations.launchConfigurations),
  {
    setPagination: (value, sorting) =>
      LaunchConfigurationsActions.setPagination({
        filters: filter(
          [
            {
              field: "emailAddress",
              value,
            },
          ],
          ({ value }) => value !== ""
        ),
        sorting,
      }),
  }
);

export type LaunchConfigurationsComponentProps = ConnectedProps<typeof connection>;

class LaunchConfigurationsComponentInner extends Component<LaunchConfigurationsComponentProps> {
  readonly subscriptions: Subscription[] = [];
  readonly emailSubject;

  constructor(props: LaunchConfigurationsComponentProps) {
    super(props);
    this.emailSubject = new Subject<string>();

    this.subscriptions.push(
      this.emailSubject.pipe(debounceTime(500)).subscribe((value) => {
        this.props.setPagination(value, this.props.pagination.sorting);
        this.setState({ debouncedEmail: value });
      })
    );
  }

  componentDidMount() {
    this.props.setPagination("", { field: "createdDate", direction: "desc" });
  }

  componentWillUnmount() {
    this.subscriptions.forEach((x) => x.unsubscribe());
  }

  state = {
    loading: false,
    email: "",
    debouncedEmail: "",
  };

  render() {
    const { data } = this.props;

    let componentToRender = null;

    switch (data.status) {
      case "error":
        componentToRender = <AnErrorHasOccured />;
        break;
      case "loading":
      case "empty":
        componentToRender = (
          <Wrapper>
            <Spinner />
          </Wrapper>
        );
        break;
      case "ready":
        componentToRender = (
          <PresentationalLaunchConfigurationList launchConfigurationList={data.data} />
        );
        break;
    }

    if (this.state.debouncedEmail !== this.state.email) {
      componentToRender = (
        <Wrapper>
          <Spinner />
        </Wrapper>
      );
    }

    return (
      <Widget>
        <Header>
          <H1 className="h1">Launch Configurations</H1>
        </Header>
        <TextField
          value={this.state.email}
          onChange={(e) => {
            this.setState({ email: e.target.value });
            this.emailSubject.next(e.target.value);
          }}
          fullWidth
          type="text"
          label="E-mail"
        />
        {componentToRender}
      </Widget>
    );
  }
}

export const LaunchConfigurationsComponent = connection(LaunchConfigurationsComponentInner);
